import BarChart from '@App/components/BarChart/BarChart';

import { useTranslation } from '@utils/i18n/i18n.consumers';
import { RISK_DATA_KASUNGU } from '@App/pages/RiskDataPage/risk-data';
import { GLOSSARY_ITEMS } from '@App/components/Navbar/GlossarySearch/glossary-search.constants';

import DefinitionComponent from '@App/components/DefinitionComponent/DefinitionComponent';
import RiskDataStories from '../RiskDataStories/RiskDataStories';

import { getMaxValue } from '../risk-data-content.utils';

import './risk-data-dry-spell.styl';

function RiskDataDrySpell({
  selectedScenario,
  selectedHorizon,
}) {
  const { t } = useTranslation();

  return (
    <div className="risk-data-dry-spell">
      <h3>{t('Introduction')}</h3>
      <p>
        {t('A dry spell is a period of abnormally dry ')}
        <DefinitionComponent
          id={72}
          term="Weather"
          definition={GLOSSARY_ITEMS.find(item => item.id === 72)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 72)?.url}
          lowercase
        />
        {t(' characterized by a prolonged deficiency of rainfall. We identify dry spells by estimating the number of consecutive dry days, i.e. the number of consecutive days when rainfall is less than 1mm per day. ')}
        <DefinitionComponent
          id={10}
          term="Climate change"
          definition={GLOSSARY_ITEMS.find(item => item.id === 10)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 10)?.url}
        />
        {t(' is increasing the frequency and magnitude of dry spells in some regions of the world.')}
      </p>
      <p>
        {t('The average number of consecutive dry days per year is shown for the historical and the future periods in Kasungu.')}
      </p>
      <BarChart
        data={RISK_DATA_KASUNGU['dry-spell'][selectedScenario.id]}
        selectedItem={selectedHorizon.id}
        title={t('Consecutive dry days per year in the scenario of {{selectedScenario}}', { selectedScenario: t(selectedScenario.label).toLowerCase() })}
        xDataKey="horizon"
        yAxisLabel={t('Days/year')}
        yDataKey="value"
        yDomain={[0, getMaxValue(RISK_DATA_KASUNGU['dry-spell'])]}
      />
      <p>
        {t('Over the recent past (1995-2024) this number was 153 days/year on average, but with a large year to year variability (38 days/year)')}
      </p>
      
      <p>
        {t(' The duration of dry spells is not projected to increase substantially in the future in Kasungu. The changes are very small for the medium emissions scenario (3 days/year at most). For the highest emission scenario, they increase to 7days/year (2025-2054) and 17 days/year (2055-2084). These increments are well within the past year to year variability. As a result, the future risk of consecutive dry days is estimated to be no higher than the current risk. Therefore, if we assume that communities are prepared to cope with the current occurrence of dry spells, and since no large changes are projected, we can assume that the risk associated with dry spells will be low in Kasungu.')}
      </p>
      <div className="how-does-this-affect-me__wrapper">
        <h3>
          {t('How does this affect me?')}
        </h3>
        <p>
          {t('The impacts of dry spells are primarily indirect, they can result in water and food shortages and loss of livelihoods. Dry spells can cause health risks such as malnutrition, infectious diseases, non-communicable diseases and mental health conditions.')}
        </p>
      </div>
      <h3>
        {t('How can we adapt?')}
      </h3>
      <p>
        {t('To tackle the risks of dry spells the following strategies can be applied:')}
        <ul>
          <li>{t('Climate resilient water and sanitation')}</li>
          <li>{t('Food and seed banks and resilient agricultural practices')}</li>
          <li>{t('Improved water and land management practices')}</li>
          <li>
            <DefinitionComponent
              id={68}
              term="Water conservation"
              definition={GLOSSARY_ITEMS.find(item => item.id === 68)?.description}
              learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 68)?.url}
            />
            {t(' management')}
          </li>
        </ul>
      </p>
      <RiskDataStories hazardCategories={['dry-spell']} />
    </div>
  );
}

export default RiskDataDrySpell;